import { useCallback, useMemo } from 'react';
import { FiCopy } from 'react-icons/fi';
import { cn } from '@/lib/utils/cn';
import { stanNotify } from '@/lib/stanNotify';
import { DataRowActionButton, StanDataColumn } from './StanDataRow.types';
import { handleLinkCopy, renderActionButton, renderColumnValue } from './StanDataRow.logic';
import styles from './StanDataRow.module.css';
import { createIconElement } from '@/lib';
import { BadgeVariant, StanBadge } from '../StanBadge';

interface UseStanDataRowRendererProps {
    columns: StanDataColumn[];
    actionButtons: DataRowActionButton[];
}

export const useStanDataRowRenderer = ({ columns, actionButtons }: UseStanDataRowRendererProps) => {
    const renderColumn = useCallback((column: StanDataColumn) => {
        const columnData = renderColumnValue(column);

        if (columnData.type === 'link') {
            const handleCopy = async () => {
                await handleLinkCopy(columnData.linkUrl, columnData.onCopy, stanNotify);
            };

            return (
                <div data-no-row-click className={styles['link-container']}>
                    <a
                        href={columnData.linkUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={styles['data-row-link-text']}
                        onClick={e => e.stopPropagation()}
                    >
                        {columnData.displayText}
                    </a>
                    {columnData.showCopyButton && (
                        <button
                            onClick={e => {
                                e.stopPropagation();
                                handleCopy();
                            }}
                            className={styles['copy-button']}
                        >
                            <FiCopy className='size-3' />
                            Copy
                        </button>
                    )}
                </div>
            );
        }

        if (columnData.type === 'pill') {
            // Map status variants to badge variants
            const variantMap: Record<string, BadgeVariant> = {
                draft: 'muted',
                sent: 'success',
                scheduled: 'primary',
                active: 'success',
                inactive: 'muted',
                error: 'destructive',
            };

            const badgeVariant = column.statusVariant ? variantMap[column.statusVariant] || 'default' : 'default';

            return (
                <StanBadge variant={badgeVariant} size='sm'>
                    {columnData.value}
                </StanBadge>
            );
        }

        return columnData.value;
    }, []);

    const renderedColumns = useMemo(
        () =>
            columns.map((column, index) => (
                <div
                    key={column.id || column.name || `column-${index}`}
                    className={cn(
                        styles.column,
                        column.onClick && styles.clickable,
                        column.type === 'pill' && styles['pill-column'],
                        column.type === 'link' && styles['link-column'],
                        index === 0 && styles['first-column'],
                        column.className
                    )}
                    onClick={column.onClick}
                    data-label={column.name}
                >
                    {column.icon &&
                        createIconElement(column.icon, {
                            className: styles['column-icon'],
                            removeFill: false,
                        })}
                    <div className={cn(styles['column-value'])}>{renderColumn(column)}</div>
                </div>
            )),
        [columns, renderColumn]
    );

    const renderedActionButtons = useMemo(
        () =>
            actionButtons.map(actionButton => {
                const buttonData = renderActionButton(actionButton, (e: React.MouseEvent) => e.stopPropagation());

                if (buttonData.type === 'dropdown') {
                    return (
                        <div key={buttonData.key} data-no-row-click onClick={buttonData.onClick}>
                            {buttonData.menu}
                        </div>
                    );
                }

                // Default button type
                return (
                    <button
                        key={buttonData.key}
                        className={styles['action-button']}
                        onClick={buttonData.onClick}
                        title={buttonData.label}
                    >
                        {buttonData.icon &&
                            createIconElement(buttonData.icon, {
                                removeFill: false,
                            })}
                    </button>
                );
            }),
        [actionButtons]
    );

    return {
        renderedColumns,
        renderedActionButtons,
    };
};
